import classNames from "classnames";
import { Inline } from "PFComponents/containers";
import { CountTag } from "PFComponents/count_tag";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { Id } from "PFTypes";
import { useNavigate } from "react-router";

import css from "./tabs.module.scss";
import { Tab, TabsVariant } from "./tabs.types";

type TabsProps<TabType> = {
  tabs: Tab<TabType>[];
  variant?: TabsVariant;
  onChange: (id: TabType) => void;
  activeTab?: Id;
  disabled?: boolean;
  className?: string;
  wrap?: boolean;
  qaId?: string;
};

export const Tabs = <TabType extends Id = Id>({
  tabs,
  activeTab,
  variant = TabsVariant.Horizontal,
  onChange,
  className,
  disabled,
  wrap,
  qaId
}: TabsProps<TabType>) => {
  const navigate = useNavigate();

  return (
    <section
      className={classNames(css.navigationContainer, className, {
        [css.vertical]: variant === TabsVariant.Vertical,
        [css.wrap]: wrap
      })}
    >
      {tabs
        .filter(({ hidden }) => !hidden)
        .map(
          ({
            title,
            id,
            subtitle,
            count,
            href,
            icon,
            iconColor,
            rightIcon,
            rightIconColor,
            spread,
            variant,
            disabled: tabDisabled,
            ariaLabel,
            ddActionName
          }) => {
            const isIdSelected = activeTab === id;
            const hasCount = count !== undefined;
            const handleOnClick = () => {
              onChange(id);
              if (href) {
                navigate(href);
              }
            };

            return (
              <nav key={id}>
                <button
                  aria-current={isIdSelected}
                  onClick={handleOnClick}
                  className={classNames(css.tabContainer, css.button, {
                    [css.selected]: isIdSelected
                  })}
                  data-qa-id={qaId ? `${qaId}.${id}` : null}
                  disabled={disabled || tabDisabled}
                  aria-label={ariaLabel || title}
                  title={ariaLabel || title}
                  data-dd-action-name={ddActionName}
                >
                  {icon && <Icon name={icon} size="sm" color={iconColor || "paletteBlue0"} />}
                  <div
                    className={classNames(css.text, {
                      [css.fullWidth]: spread
                    })}
                  >
                    <Inline
                      alignItems="center"
                      justifyContent={spread ? "space-between" : undefined}
                      className={css.fullWidth}
                    >
                      <Typography
                        variant={isIdSelected ? "bodyBold" : "bodyRegular"}
                        className={classNames(css.title, {
                          [css.selected]: isIdSelected
                        })}
                        wrapText
                      >
                        {title}
                      </Typography>
                      {(hasCount || rightIcon) && (
                        <Inline center>
                          {hasCount && (
                            <CountTag highlight={!!isIdSelected} count={count} variant={variant} />
                          )}
                          {rightIcon && <Icon name={rightIcon} size="sm" color={rightIconColor} />}
                        </Inline>
                      )}
                    </Inline>
                    <Typography variant="labelRegular" className={css.title}>
                      {subtitle}
                    </Typography>
                  </div>
                </button>
              </nav>
            );
          }
        )}
    </section>
  );
};
